/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useEffect, useState } from 'react';
import { Download, Share, SquarePlus, X } from 'lucide-react';

const DISMISS_KEY = 'udyam_install_prompt_dismissed_at';
const DISMISS_DAYS = 14;

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isIosSafari() {
    const ua = window.navigator.userAgent;
    return /iphone|ipad|ipod/i.test(ua) && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

function wasDismissedRecently() {
    try {
        const ts = Number(localStorage.getItem(DISMISS_KEY));
        return ts > 0 && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    }
    catch {
        return false;
    }
}

// Android/Chrome fires `beforeinstallprompt` and lets us trigger the native
// install dialog directly. iOS Safari never fires that event — there is no
// API to add a home-screen shortcut programmatically there — so it gets a
// manual "tap Share, then Add to Home Screen" instruction banner instead.
export default function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showIosHint, setShowIosHint] = useState(false);
    const [dismissed, setDismissed] = useState(() => wasDismissedRecently());

    useEffect(() => {
        if (isStandalone() || dismissed)
            return;
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstall);
        if (isIosSafari())
            setShowIosHint(true);
        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }, [dismissed]);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
        catch { /* storage unavailable — no-op, banner just re-shows next visit */ }
        setDismissed(true);
        setDeferredPrompt(null);
        setShowIosHint(false);
    };

    const handleInstall = async () => {
        if (!deferredPrompt)
            return;
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        setDeferredPrompt(null);
    };

    if (dismissed || (!deferredPrompt && !showIosHint))
        return null;

    return (<div className="sm:hidden fixed inset-x-3 z-[70] animate-fade-in" style={{ bottom: 'calc(4.75rem + env(safe-area-inset-bottom))' }} id="install-prompt-banner">
      <div className="bg-slate-900 text-white rounded-2xl shadow-xl p-3.5 flex items-center gap-3">
        <img src="/logo192.png" alt="" className="w-10 h-10 rounded-xl shrink-0" aria-hidden="true"/>
        {deferredPrompt ? (<>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold leading-snug">Install UdyamConnect</p>
              <p className="text-[10px] text-slate-400 leading-snug">Add to your home screen for quick access</p>
            </div>
            <button type="button" onClick={handleInstall} id="btn-install-app" className="shrink-0 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 cursor-pointer">
              <Download className="w-3.5 h-3.5"/> Install
            </button>
          </>) : (<div className="min-w-0 flex-1">
            <p className="text-xs font-bold leading-snug">Install UdyamConnect</p>
            <p className="text-[10px] text-slate-400 leading-snug flex items-center gap-1 flex-wrap">
              Tap <Share className="w-3 h-3 inline shrink-0"/> then "Add to Home Screen" <SquarePlus className="w-3 h-3 inline shrink-0"/>
            </p>
          </div>)}
        <button type="button" onClick={dismiss} title="Dismiss" id="btn-dismiss-install-prompt" className="shrink-0 p-1 text-slate-400 hover:text-white cursor-pointer">
          <X className="w-4 h-4"/>
        </button>
      </div>
    </div>);
}
